import { useCallback, useMemo, useRef, useState } from 'react'

import { useAppSelector } from 'state/store'

import {
  ActivityValue,
  AutocompleteActivitySourceConfig,
  AutocompleteSuggestion,
  FlatInteractionModelData,
  NestableGroupValue
} from 'types/shared'
import { FetchSuggestionsHook } from './useSuggestionsFromSources'
import { YesObject } from '@yes.technology/react-toolkit'

function toStringValue(value: ActivityValue | undefined): string | null {
  if (value === undefined || value === null) return null
  if (typeof value === 'string') return value
  if (typeof value === 'object' && 'des' in value) return value.des
  return null
}

function findLabelInValues(
  values: Record<string, NestableGroupValue | ActivityValue>,
  labelUuid: string
): string | null {
  if (labelUuid in values) {
    return toStringValue(values[labelUuid] as ActivityValue)
  }

  for (const key of Object.keys(values)) {
    const value = values[key]
    if (Array.isArray(value)) {
      for (const child of value) {
        const found = findLabelInValues(child.values, labelUuid)
        if (found) return found
      }
    }
  }

  return null
}

const useActivitySuggestions: FetchSuggestionsHook = ({ dataSources }) => {
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])

  const interactionModel = useAppSelector((state) => state.interactionModel)

  const activityValues = interactionModel?.flatData
    ?.activity_values as FlatInteractionModelData['activity_values']

  const activityValuesRef = useRef(activityValues)
  activityValuesRef.current = activityValues

  const activitySources = useMemo(
    () =>
      (dataSources || []).filter(
        (
          dataSource
        ): dataSource is {
          type: 'activity'
          config: AutocompleteActivitySourceConfig
        } => dataSource?.type === 'activity'
      ),
    [dataSources]
  )

  const fetchSuggestions = useCallback(
    (value: string) => {
      const terms = (value || '').toLowerCase()
      const results: YesObject[] = []

      for (const source of activitySources) {
        const groupUuid = source.config.group_activitymodel_uuid
        const labelUuid = source.config.label_activitymodel_uuid

        const group = activityValuesRef.current?.[groupUuid] as
          | NestableGroupValue
          | undefined
        if (!Array.isArray(group)) continue

        for (const row of group) {
          const label = findLabelInValues(row.values, labelUuid)
          if (!label) continue
          const labelLc = label.toLowerCase()
          if (terms && !labelLc.includes(terms)) continue
          results.push({
            uuid: `activity_group-${row.uuid}`,
            des: label
          } as YesObject)
        }
      }

      const seen = new Set<string>()
      const deduped = results.filter((r) => {
        const key = `${r.uuid}::${r.des}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      setSuggestions(deduped)
    },
    [activitySources]
  )

  const resetSuggestions = useCallback(() => {
    setSuggestions([])
  }, [setSuggestions])

  return {
    suggestions,
    setSuggestions,
    fetchSuggestions,
    debouncedFetchSuggestions: fetchSuggestions,
    isLoading: false,
    resetSuggestions
  }
}

export default useActivitySuggestions
